import React, { useEffect, useState } from 'react';
import { Form, ButtonGroup, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useApi } from './ApiState';
import { fire } from '../../../prompt';
import withInputs from '../../../hoc/form';
import { buildPage } from '../Page';

function Submit({ submit, onSubmit, submitting, canSubmit, index }) {
	const buttons = submit.values || [submit];
	return <>
		{buttons.map((button, i) => <input
			key={i}
			type={index === i ? 'submit' : 'button'}
			className={button.className || 'btn btn-success'}
			value={submitting && button.submittingValue || button.value}
			onClick={onSubmit(i)}
			disabled={submitting || !canSubmit(button.require)} />)}
	</>;
}

function FormModal({ modal, setModalIndex, i, doSubmit }) {
	const toggle = e => {
		e?.preventDefault?.();
		setModalIndex(null);
	};
	return <Modal isOpen toggle={toggle}>
		{modal.title && <ModalHeader toggle={toggle}>{buildPage(modal.title)}</ModalHeader>}
		{modal.html && <ModalBody children={buildPage(modal.html)} />}
		<ModalFooter>
			<Button onClick={toggle} color="secondary">{modal.cancelButtonText}</Button>
			<Button onClick={e => {
				e.preventDefault();
				doSubmit(i);
				if(!modal.stayOpen) {
					setModalIndex(null);
				}
			}} color="primary">{modal.confirmButtonText}</Button>
		</ModalFooter>
	</Modal>;
}

const KB = 1024;
const MB = KB * KB;
const GB = MB * KB;
const DIGITS = 10;

function getSizeString(size, t) {
	if(size >= GB) {
		return t('GiB', { size: Math.round(size * DIGITS / GB) / DIGITS });
	} else if(size >= MB) {
		return t('MiB', { size: Math.round(size * DIGITS / MB) / DIGITS });
	} else if(size >= KB) {
		return t('KiB', { size: Math.round(size * DIGITS / KB) / DIGITS });
	}
	return t('bytes', { size });
}

function PortalForm({ renderInputs, submit, children, buttons, autoSubmit, canSubmit, values, forceFormData, prompt, modal, context, buttonGroupProps, maxFileSize, submitting, setSubmitting, ...props }) {
	const { t } = useTranslation();
	const { submitForm, callRawMethod } = useApi();
	const [first, setFirst] = useState(true);
	const [modalIndex, setModalIndex] = useState(null);
	const doSubmit = i => {
		if(!submit || !submit.post) {
			return;
		}
		setSubmitting(true);
		let data = values;
		const submitButtons = submit.values || [submit];
		if(submitButtons[i]?.data) {
			data = { ...data, ...submitButtons[i].data };
		}
		let fileSizeCheck;
		if(maxFileSize) {
			fileSizeCheck = size => {
				if(size > maxFileSize) {
					window.alert(t('FileSize.Max', { size: getSizeString(maxFileSize, t) }));
					return false;
				}
				return true;
			};
		}
		if(submit.raw) {
			callRawMethod('post', submit.post, context, data, fileSizeCheck);
			return setSubmitting(false);
		}
		submitForm(context, submit.post, data, forceFormData, fileSizeCheck).then(() => {
			setSubmitting(false);
		});
	};
	const submitButtons = submit.values || [submit];
	const onSubmit = (i, check) => {
		return e => {
			e.preventDefault();
			if(check) {
				if(submitting) {
					return;
				}
				if(!canSubmit(submitButtons[i]?.require)) {
					return;
				}
			}
			const p = submitButtons[i]?.prompt || prompt;
			if(p) {
				return fire(p).then(res => {
					if(res.value) {
						doSubmit(i);
					}
				});
			}
			if(submitButtons[i]?.modal || modal) {
				setModalIndex(i);
				return;
			}
			doSubmit(i);
		};
	};
	const index = submit?.defaultSubmit || 0;
	useEffect(() => {
		if(first) {
			return setFirst(false);
		}
		setSubmitting(false);
		if(autoSubmit) {
			doSubmit(index);
		}
	}, [values]);
	return <Form {...props} onSubmit={onSubmit(index, true)}>
		{renderInputs()}
		{children}
		{!autoSubmit && <ButtonGroup {...buttonGroupProps}>
			{buttons && buttons.map(buildPage)}
			{submit && <Submit submit={submit} onSubmit={onSubmit} submitting={submitting} canSubmit={canSubmit} index={index} />}
		</ButtonGroup>}
		{modalIndex !== null && <FormModal modal={submitButtons[modalIndex]?.modal || modal} setModalIndex={setModalIndex} i={modalIndex} doSubmit={doSubmit} />}
	</Form>;
}

export default withInputs(PortalForm);
